import { useState, useEffect, type FormEvent } from 'react';
import { api, ApiRequestError } from '../../lib/api';

interface Scenario {
  id: string;
  display_name: string;
  description: string;
  prompt_template: string;
  is_enabled: boolean;
  created_at: string;
}

interface Template {
  id: string;
  scenario_id: string;
  name: string;
  prompt: string;
  image_size: string;
  quality_level: string;
  num_variants: number;
  is_enabled: boolean;
}

export function AdminScenarios() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState<'scenario' | 'template' | null>(null);
  const [editingScenario, setEditingScenario] = useState<Scenario | null>(null);

  // Scenario form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTemplate, setFormTemplate] = useState('');

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.get<{ scenarios: Scenario[] }>('/admin/scenarios').catch(() => ({ data: { scenarios: [] as Scenario[] } })),
      api.get<{ items: Template[] }>('/templates', { per_page: '100' }).catch(() => ({ data: { items: [] as Template[] } })),
    ]).then(([scenRes, tmplRes]) => {
      setScenarios(scenRes.data?.scenarios ?? []);
      setTemplates(tmplRes.data?.items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateScenario = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingScenario) {
        await api.patch(`/admin/scenarios/${editingScenario.id}`, {
          display_name: formName,
          description: formDescription,
          prompt_template: formTemplate,
        });
      } else {
        await api.post('/admin/scenarios', {
          display_name: formName,
          description: formDescription,
          prompt_template: formTemplate,
        });
      }
      setShowForm(null);
      setEditingScenario(null);
      setFormName('');
      setFormDescription('');
      setFormTemplate('');
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
    }
  };

  const handleDeleteScenario = async (id: string) => {
    if (!confirm('Delete this scenario?')) return;
    try {
      await api.delete(`/admin/scenarios/${id}`);
      fetchData();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
    }
  };

  const startEdit = (s: Scenario) => {
    setEditingScenario(s);
    setFormName(s.display_name);
    setFormDescription(s.description);
    setFormTemplate(s.prompt_template);
    setShowForm('scenario');
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Scenarios & Templates</h1>
        <button
          onClick={() => {
            setEditingScenario(null);
            setFormName('');
            setFormDescription('');
            setFormTemplate('');
            setShowForm('scenario');
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
        >
          New Scenario
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}

      {/* Scenario Form */}
      {showForm === 'scenario' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingScenario ? 'Edit Scenario' : 'New Scenario'}
          </h2>
          <form onSubmit={handleCreateScenario} className="space-y-4">
            <div>
              <label htmlFor="scenarioName" className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                id="scenarioName"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="scenarioDesc" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="scenarioDesc"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="scenarioTemplate" className="block text-sm font-medium text-gray-700 mb-1">
                Prompt Template
              </label>
              <textarea
                id="scenarioTemplate"
                value={formTemplate}
                onChange={(e) => setFormTemplate(e.target.value)}
                required
                rows={3}
                placeholder="Use {{prompt}} where the user's prompt should be inserted"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
              >
                {editingScenario ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 min-h-[44px]"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Scenarios List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Scenarios</h2>
        </div>
        {scenarios.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No scenarios configured</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scenarios.map((s) => (
              <div key={s.id} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium text-gray-900">{s.display_name}</div>
                  <div className="text-xs text-gray-500">{s.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${s.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {s.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => startEdit(s)}
                    className="text-xs text-blue-600 hover:text-blue-700 min-h-[44px] px-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteScenario(s.id)}
                    className="text-xs text-red-600 hover:text-red-700 min-h-[44px] px-2"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
        </div>
        {templates.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No templates configured</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {templates.map((t) => (
              <div key={t.id} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-500">
                    {t.image_size} &middot; {t.quality_level} &middot; {t.num_variants} variant(s)
                  </div>
                </div>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${t.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                >
                  {t.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
